<script setup lang="ts">
    import { ref, defineEmits } from 'vue';
    import membersService from '@/services/membersService';

    const emit = defineEmits(['completed']);

    const loading = ref(false);
    const errorMessage = ref('');
    const importFile = ref<File | null>(null);
    const importDatas = ref([] as Array<any>);
    const showImportSection = ref(false);

    async function downloadCsv() {
        try {
            const blob = await membersService.downloadCsvTemplate();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "collectivite_membres_template.csv");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Erreur de téléchargement :", error);
            errorMessage.value = "Erreur lors du téléchargement du modèle CSV";
        }
    }

    async function onCsvImportFileLoaded(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            await doPrepareImportFile(file);
            input.value = '';
        }
    }

    async function doPrepareImportFile(csvFile: File) {
        try {
            loading.value = true;
            errorMessage.value = '';
            importFile.value = null;
            importDatas.value = [];

            const rows = await membersService.prepareImportFile(csvFile);
            importDatas.value = rows || [];
            importFile.value = csvFile;
            showImportSection.value = true;

            if (importDatas.value.length === 0) {
                errorMessage.value = "Aucune donnée valide trouvée dans le fichier";
            }
        } catch (error: any) {
            errorMessage.value = error.message || "Erreur lors de la préparation du fichier";
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

    async function doImportAction() {
        if (importDatas.value.length === 0) { return; }

        try {
            loading.value = true;
            errorMessage.value = '';
            const dataToImport = importDatas.value.map((row: any) => row.data || row);
            await membersService.importFileDatas(dataToImport);
            
            importFile.value = null;
            importDatas.value = [];
            showImportSection.value = false;
            
            emit('completed');
        } catch(error: any) {
            errorMessage.value = error.message || "Erreur lors de l'importation";
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

    function cancelImport() {
        importFile.value = null;
        importDatas.value = [];
        showImportSection.value = false;
        errorMessage.value = '';
    }
</script>

<template>
    <div>
        <!-- Hidden file input -->
        <input id="file-upload-membres-frontoffice" name="file-upload" type="file" accept=".csv" @change="onCsvImportFileLoaded" class="display-none" />
        
        <!-- Loading overlay -->
        <div style="display: flex; justify-content: center" v-if="loading">
            <div class="loader dark"></div>
        </div>

        <!-- Import section (shown after file is selected) -->
        <div v-if="showImportSection && importFile && !loading" class="widget px-6 py-7 mb-5">
            <div class="s14_mobile mb-4">
                <h3 class="secondary-font gap-4 mt-0 mb-2">
                    Fichier: {{ importFile.name }}
                </h3>
                <p class="mb-0">Nombre de membres à importer: <strong>{{ importDatas.length }}</strong></p>
            </div>

            <div v-if="errorMessage" class="text-red mb-4">{{ errorMessage }}</div>

            <div class="flex gap-3">
                <button type="button" @click="doImportAction" class="btn lg w-100" :disabled="loading">
                    <img src="/public/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                    <span>Importer {{ importDatas.length }} membre(s)</span>
                </button>
                <button type="button" @click="cancelImport" class="btn lg white2">
                    <span>Annuler</span>
                </button>
            </div>
        </div>
    </div>
</template>
